import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Polygon, Popup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import api from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useAuthStore } from '../../store/authStore';

// Fix Leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Boundary {
  id: string;
  name: string;
  description?: string;
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  area_hectares?: number;
  monitoring_enabled: boolean;
  monitoring_frequency: string;
  tags?: string[];
  created_at: string;
}

interface BoundaryStats {
  total_boundaries: number;
  active_boundaries: number;
  total_area_hectares: number;
  avg_area_hectares: number;
}

// Component to fit map bounds to boundaries
const FitBounds: React.FC<{ boundaries: Boundary[] }> = ({ boundaries }) => {
  const map = useMap();

  useEffect(() => {
    if (boundaries.length > 0) {
      const bounds = L.latLngBounds(
        boundaries.flatMap(b => 
          b.geometry.coordinates[0].map(coord => [coord[1], coord[0]] as [number, number])
        )
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [boundaries, map]);

  return null;
};

export const BoundariesMapPage: React.FC = () => {
  const { user } = useAuthStore();
  const [boundaries, setBoundaries] = useState<Boundary[]>([]);
  const [stats, setStats] = useState<BoundaryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [newBoundaryName, setNewBoundaryName] = useState('');
  const [newBoundaryDescription, setNewBoundaryDescription] = useState('');
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [drawnLayer, setDrawnLayer] = useState<any>(null);
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Map center (default to India)
  const [mapCenter] = useState<[number, number]>([22.5937, 78.9629]);
  const [mapZoom] = useState(5);

  useEffect(() => {
    loadBoundaries();
    loadStats();
  }, []);

  const loadBoundaries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/satellite/boundaries');
      setBoundaries(response.data.data.boundaries);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load boundaries');
      console.error('Error loading boundaries:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/satellite/boundaries/stats');
      setStats(response.data.data.stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleDrawCreated = (e: any) => {
    const layer = e.layer;
    setDrawnLayer(layer);
    setShowNameDialog(true);
    setIsDrawing(false);
  };

  const saveBoundary = async () => {
    if (!newBoundaryName.trim()) {
      setError('Boundary name is required');
      return;
    }

    if (!drawnLayer) {
      setError('No boundary drawn');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Convert Leaflet layer to GeoJSON
      const geoJSON = drawnLayer.toGeoJSON();
      
      await api.post('/satellite/boundaries', {
        name: newBoundaryName,
        description: newBoundaryDescription,
        geometry: geoJSON.geometry,
        monitoring_enabled: true,
        monitoring_frequency: 'weekly',
        tags: ['manual'],
      });

      setSuccess('Boundary created successfully');
      setShowNameDialog(false);
      setNewBoundaryName('');
      setNewBoundaryDescription('');
      setDrawnLayer(null);
      
      // Reload boundaries
      await loadBoundaries();
      await loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create boundary');
      console.error('Error creating boundary:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteBoundary = async (id: string) => {
    if (!confirm('Are you sure you want to delete this boundary?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await api.delete(`/satellite/boundaries/${id}`);
      
      setSuccess('Boundary deleted successfully');
      await loadBoundaries();
      await loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete boundary');
      console.error('Error deleting boundary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      const text = await file.text();
      const geoJSON = JSON.parse(text);

      await api.post('/satellite/boundaries/import', { geoJSON });

      setSuccess(`Imported boundaries from ${file.name}`);
      await loadBoundaries();
      await loadStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to import boundaries');
      console.error('Error importing boundaries:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const exportBoundaries = async () => {
    try {
      const response = await api.get('/satellite/boundaries/export');
      const geoJSON = response.data.data.geoJSON;

      // Create download link
      const blob = new Blob([JSON.stringify(geoJSON, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `boundaries-${new Date().toISOString().split('T')[0]}.geojson`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess('Boundaries exported successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export boundaries');
      console.error('Error exporting boundaries:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Satellite Monitoring Boundaries
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Define areas of interest for automated satellite monitoring
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".geojson,.json,.kml"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            disabled={uploading}
          >
            {uploading ? <Spinner size="sm" /> : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            )}
            Import GeoJSON
          </Button>
          <Button
            onClick={exportBoundaries}
            variant="outline"
            disabled={boundaries.length === 0}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Export GeoJSON
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-100 dark:bg-primary-900 rounded-md p-3">
                <svg className="h-6 w-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Boundaries</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_boundaries}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 dark:bg-green-900 rounded-md p-3">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Monitoring</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.active_boundaries}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900 rounded-md p-3">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Area</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {stats.total_area_hectares?.toFixed(1) || 0} ha
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900 rounded-md p-3">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Area</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {stats.avg_area_hectares?.toFixed(1) || 0} ha
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <Alert type="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Name Dialog */}
      {showNameDialog && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Name Your Boundary
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={newBoundaryName}
                onChange={(e) => setNewBoundaryName(e.target.value)}
                placeholder="e.g., East Kolkata Wetlands"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={newBoundaryDescription}
                onChange={(e) => setNewBoundaryDescription(e.target.value)}
                placeholder="Optional description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveBoundary} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Save Boundary'}
              </Button>
              <Button
                onClick={() => {
                  setShowNameDialog(false);
                  setNewBoundaryName('');
                  setNewBoundaryDescription('');
                  setDrawnLayer(null);
                }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Map */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" style={{ height: '600px' }}>
        {loading && boundaries.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <Spinner size="lg" />
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <FeatureGroup>
              <EditControl
                position="topright"
                onCreated={handleDrawCreated}
                draw={{
                  rectangle: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                  polygon: {
                    allowIntersection: false,
                    showArea: true,
                  },
                }}
                edit={{
                  edit: false,
                  remove: false,
                }}
              />
            </FeatureGroup>

            {/* Render existing boundaries */}
            {boundaries.map((boundary) => (
              <Polygon
                key={boundary.id}
                positions={boundary.geometry.coordinates[0].map(coord => [coord[1], coord[0]])}
                pathOptions={{
                  color: boundary.monitoring_enabled ? '#10b981' : '#6b7280',
                  fillColor: boundary.monitoring_enabled ? '#10b981' : '#6b7280',
                  fillOpacity: 0.2,
                }}
              >
                <Popup>
                  <div className="p-2">
                    <h4 className="font-semibold text-gray-900">{boundary.name}</h4>
                    {boundary.description && (
                      <p className="text-sm text-gray-600 mt-1">{boundary.description}</p>
                    )}
                    <div className="mt-2 space-y-1 text-sm">
                      <p><strong>Area:</strong> {boundary.area_hectares?.toFixed(2)} hectares</p>
                      <p><strong>Monitoring:</strong> {boundary.monitoring_enabled ? 'Active' : 'Inactive'}</p>
                      <p><strong>Frequency:</strong> {boundary.monitoring_frequency}</p>
                    </div>
                    <button
                      onClick={() => deleteBoundary(boundary.id)}
                      className="mt-3 w-full px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </Popup>
              </Polygon>
            ))}

            <FitBounds boundaries={boundaries} />
          </MapContainer>
        )}
      </div>

      {/* Instructions */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          How to Use
        </h3>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start">
            <svg className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span><strong>Draw Boundary:</strong> Click the polygon tool (⬟) on the map, then click to place points around your area of interest. Double-click to finish.</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span><strong>Import:</strong> Upload GeoJSON, KML, or Shapefile to bulk import boundaries.</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span><strong>Monitor:</strong> Green boundaries are actively monitored for satellite imagery updates. Gray boundaries are inactive.</span>
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 mr-2 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span><strong>Export:</strong> Download all boundaries as GeoJSON for use in other GIS tools.</span>
          </li>
        </ul>
      </Card>
    </div>
  );
};

// Made with Bob
